import { inject, Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { AGMediaKeys } from '@ag-common-lib/lib/models/utils/ag-media.model';
import {
  AGENCY_SYSTEM_DIRECTORY_NAMES,
  AgencyFileDirectory,
  AgencyFileDirectoryKeys,
} from '@ag-common-lib/lib/models/utils/agency-file-directory.model';
import { AgencyMedia, AgencyMediaKeys } from '@ag-common-lib/lib/models/utils/agency-media.model';
import { FileDirectoryKeys, SystemFileDirectories } from '@ag-common-lib/lib/models/utils/file-directory.model';
import { FetchOptions } from '../dao/CommonFireStoreDao.dao';
import { AgencyFileDirectoriesService } from './agency-file-directories.service';
import { AgencyFilesMediaService } from './agency-files-media.service';
import { AgencyMediaService } from './agency-media.service';

const SERVER_READ: FetchOptions = { ignoreCash: true };

/**
 * Agency logo uploads are shown from the Images file directory.
 * Older uploads live on agencies/{id}/media and are copied into Images so the Media tab can manage them.
 */
@Injectable({
  providedIn: 'root',
})
export class AgencyImagesService {
  private readonly agencyFileDirectoriesService = inject(AgencyFileDirectoriesService);
  private readonly agencyFilesMediaService = inject(AgencyFilesMediaService);
  private readonly agencyMediaService = inject(AgencyMediaService);
  private readonly prepareInFlight = new Map<string, Promise<void>>();

  /**
   * Live list of files in the agency Images folder.
   * Prepares that folder first so logo uploads are present before the list is subscribed.
   */
  fetchAgencyImages(agencyDbId: string): Observable<AgencyMedia[]> {
    if (!agencyDbId) {
      return of([]);
    }

    return from(this.ensureImagesDirectory(agencyDbId)).pipe(
      switchMap(() => this.readImagesDirectory(agencyDbId)),
      switchMap(imagesDirectory => {
        const directoryId = imagesDirectory?.[BaseModelKeys.dbId];
        if (!directoryId) {
          return of([]);
        }

        return this.agencyFilesMediaService.getList(agencyDbId, directoryId);
      }),
    );
  }

  async addAgencyMediaToImagesDirectory(agencyId: string, mediaData: AgencyMedia): Promise<AgencyMedia | void> {
    await this.ensureImagesDirectory(agencyId);
    const imagesDirectory = await this.readImagesDirectory(agencyId);
    const directoryId = imagesDirectory?.[BaseModelKeys.dbId];
    if (!directoryId) {
      throw new Error('Images directory not found.');
    }

    return this.agencyFilesMediaService.create(agencyId, directoryId, mediaData, true);
  }

  /**
   * Creates the system Images and Documents folders when they are missing,
   * then copies gallery uploads that are not already in Images.
   * Calls for the same agency share one in-flight run.
   */
  ensureImagesDirectory(agencyId: string): Promise<void> {
    if (!agencyId) {
      return Promise.resolve();
    }

    const inFlight = this.prepareInFlight.get(agencyId);
    if (inFlight) {
      return inFlight;
    }

    const task = this.ensureDirectoriesAndCopyLegacyMedia(agencyId).finally(() => {
      this.prepareInFlight.delete(agencyId);
    });
    this.prepareInFlight.set(agencyId, task);
    return task;
  }

  /**
   * Removes gallery records that use the same storage path.
   * Stops a deleted Images file from being copied back the next time the folder is prepared.
   */
  async removeLegacyGalleryMedia(agencyId: string, wasabiPath: string | null | undefined): Promise<void> {
    if (!agencyId || !wasabiPath) {
      return;
    }

    const legacyMedia = await firstValueFrom(this.agencyMediaService.getList(agencyId, [], SERVER_READ));
    const matches = (legacyMedia ?? []).filter(item => item?.[AGMediaKeys.wasabiPath] === wasabiPath);

    await Promise.all(
      matches.map(item => {
        const documentId = item?.[BaseModelKeys.dbId];
        if (!documentId) {
          return Promise.resolve();
        }

        return this.agencyMediaService.delete(agencyId, documentId, true);
      }),
    );
  }

  private async ensureDirectoriesAndCopyLegacyMedia(agencyId: string): Promise<void> {
    let fileDirectories = await this.readDirectories(agencyId);
    const existingNames = new Set(fileDirectories.map(directory => directory?.[FileDirectoryKeys.name]));
    const missingNames = AGENCY_SYSTEM_DIRECTORY_NAMES.filter(name => !existingNames.has(name));

    if (missingNames.length) {
      await Promise.all(missingNames.map(name => this.createFileDirectory(agencyId, name)));
      fileDirectories = await this.readDirectories(agencyId);
    }

    const imagesDirectory = this.findImagesDirectory(fileDirectories);
    const directoryId = imagesDirectory?.[BaseModelKeys.dbId];
    if (!directoryId) {
      return;
    }

    const [imagesMedia, legacyMedia] = await Promise.all([
      firstValueFrom(this.agencyFilesMediaService.getList(agencyId, directoryId, [], SERVER_READ)),
      firstValueFrom(this.agencyMediaService.getList(agencyId, [], SERVER_READ)),
    ]);
    const existingPaths = new Set(
      (imagesMedia ?? []).map(item => item?.[AGMediaKeys.wasabiPath]).filter((path): path is string => !!path),
    );
    const missingMedia = (legacyMedia ?? []).filter(item => {
      const path = item?.[AGMediaKeys.wasabiPath];
      return !!path && !existingPaths.has(path);
    });

    await Promise.all(missingMedia.map(item => this.copyLegacyMedia(agencyId, directoryId, item)));
  }

  private readDirectories(agencyId: string): Promise<AgencyFileDirectory[]> {
    return firstValueFrom(this.agencyFileDirectoriesService.getList(agencyId, [], SERVER_READ)).then(
      directories => directories ?? [],
    );
  }

  private async readImagesDirectory(agencyId: string): Promise<AgencyFileDirectory | null> {
    const fileDirectories = await this.readDirectories(agencyId);
    return this.findImagesDirectory(fileDirectories);
  }

  private findImagesDirectory(fileDirectories: AgencyFileDirectory[]): AgencyFileDirectory | null {
    return (
      fileDirectories.find(directory => directory?.[FileDirectoryKeys.name] === SystemFileDirectories.Images) ?? null
    );
  }

  private createFileDirectory(agencyId: string, directoryName: string): Promise<void | AgencyFileDirectory> {
    const directory: AgencyFileDirectory = {
      [AgencyFileDirectoryKeys.agencyDbId]: agencyId,
      [FileDirectoryKeys.name]: directoryName,
      [FileDirectoryKeys.isSystem]: true,
    };

    return this.agencyFileDirectoriesService.create(agencyId, directory, true);
  }

  private copyLegacyMedia(agencyId: string, directoryId: string, media: AgencyMedia): Promise<void | AgencyMedia> {
    const copy = Object.assign(new AgencyMedia(agencyId), {
      [AGMediaKeys.contentType]: media?.[AGMediaKeys.contentType] ?? null,
      [AGMediaKeys.wasabiPath]: media?.[AGMediaKeys.wasabiPath] ?? null,
      [AGMediaKeys.fileName]: media?.[AGMediaKeys.fileName] ?? null,
    });

    if (media?.[AGMediaKeys.fileSize] != null) {
      copy[AGMediaKeys.fileSize] = media[AGMediaKeys.fileSize];
    }

    if (media?.[AGMediaKeys.aspectRation] != null) {
      copy[AGMediaKeys.aspectRation] = media[AGMediaKeys.aspectRation];
    }

    return this.agencyFilesMediaService.create(agencyId, directoryId, copy, true);
  }
}
